import { useState, useRef, useCallback, useEffect } from "react";
import { requestAutoFix, type PreviousAttempt, type ReferenceFile as ApiReferenceFile } from "@/services/apex/autoFixService";

export interface AutoFixState {
  status: "idle" | "fixing" | "success" | "failed";
  attempt: number;
  maxAttempts: number;
  explanation: string | null;
  lastError: string | null;
}

interface UseAutoFixLoopOptions {
  maxAttempts?: number;
  onCodeFixed: (fixedCode: string) => void;
  onSave: () => Promise<void>;
  fileName?: string;
  metadataType?: string;
  orgId?: string;
  /** Returns other open files' content for auto-fix context */
  getReferenceFiles?: () => ApiReferenceFile[];
}

const INITIAL_STATE: AutoFixState = {
  status: "idle",
  attempt: 0,
  maxAttempts: 5,
  explanation: null,
  lastError: null,
};

export function useAutoFixLoop(options: UseAutoFixLoopOptions) {
  const { maxAttempts = 5, onCodeFixed, onSave, fileName, metadataType, orgId, getReferenceFiles } = options;

  const [autoFixState, setAutoFixState] = useState<AutoFixState>({
    ...INITIAL_STATE,
    maxAttempts,
  });

  const onCodeFixedRef = useRef(onCodeFixed);
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onCodeFixedRef.current = onCodeFixed;
    onSaveRef.current = onSave;
  }, [onCodeFixed, onSave]);

  const cancelledRef = useRef(false);
  const isRunningRef = useRef(false);

  const startAutoFix = useCallback(
    async (code: string, errorMessage: string) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;
      cancelledRef.current = false;

      const AUTO_FIX_TIMEOUT_MS = 30000; // Bug fix: 30s timeout to prevent infinite blocking

      /** Races a promise against a timeout; rejects with a descriptive Error on timeout. */
      const withTimeout = <T>(promise: Promise<T>, label: string): Promise<T> =>
        Promise.race([
          promise,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`${label} timed out after ${AUTO_FIX_TIMEOUT_MS / 1000}s`)), AUTO_FIX_TIMEOUT_MS)
          ),
        ]);

      let currentCode = code;
      let currentError = errorMessage;
      const previousAttempts: PreviousAttempt[] = [];

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelledRef.current) break;

        setAutoFixState({ status: "fixing", attempt, maxAttempts, explanation: null, lastError: currentError });

        try {
          const referenceFiles: ApiReferenceFile[] | undefined = getReferenceFiles?.();
          const result = await withTimeout(requestAutoFix({
            code: currentCode,
            error_message: currentError,
            file_name: fileName,
            metadata_type: metadataType,
            attempt,
            previous_attempts: previousAttempts.length > 0 ? previousAttempts : undefined,
            org_id: orgId,
            reference_files: referenceFiles?.length ? referenceFiles : undefined,
          }), "requestAutoFix");

          if (cancelledRef.current) break;

          if (result.status === "error" || !result.fixed_code) {
            previousAttempts.push({ attempt, error: result.error || currentError, explanation: null });
            currentError = result.error || currentError;
            continue;
          }

          currentCode = result.fixed_code;
          onCodeFixedRef.current(result.fixed_code);

          await new Promise((resolve) => setTimeout(resolve, 500));
          if (cancelledRef.current) break;

          try {
            await withTimeout(onSaveRef.current(), "onSave");
            if (cancelledRef.current) break; // Bug fix: check cancelled after await
            setAutoFixState({ status: "success", attempt, maxAttempts, explanation: result.explanation, lastError: null });
            isRunningRef.current = false;
            return;
          } catch (saveError: unknown) {
            if (cancelledRef.current) break; // Bug fix: check cancelled after await
            const errMsg = saveError instanceof Error ? saveError.message : String(saveError);
            previousAttempts.push({ attempt, error: errMsg, explanation: result.explanation });
            currentError = errMsg;
          }
        } catch (fetchError: unknown) {
          if (cancelledRef.current) break; // Bug fix: check cancelled after await
          const errMsg = fetchError instanceof Error ? fetchError.message : String(fetchError);
          previousAttempts.push({ attempt, error: errMsg, explanation: null });
          currentError = errMsg;
        }
      }

      if (!cancelledRef.current) {
        setAutoFixState({ status: "failed", attempt: maxAttempts, maxAttempts, explanation: null, lastError: currentError });
      }
      isRunningRef.current = false;
    },
    [maxAttempts, fileName, metadataType, orgId, getReferenceFiles]
  );

  const cancelAutoFix = useCallback(() => {
    cancelledRef.current = true;
    isRunningRef.current = false;
    setAutoFixState((prev) => ({ ...prev, status: "idle", lastError: null }));
  }, []);

  const resetAutoFix = useCallback(() => {
    cancelledRef.current = true;
    isRunningRef.current = false;
    setAutoFixState({ ...INITIAL_STATE, maxAttempts });
  }, [maxAttempts]);

  return { autoFixState, startAutoFix, cancelAutoFix, resetAutoFix };
}
